import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  FirearmOwnerDTO,
  MemberFirearmDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { useFirearmOwners } from '../../hooks/useFirearmOwnerApi';
import { FirearmOwnerConstants } from '../../constants/FirearmOwnerConstants';
import { getRoles } from '../../utils/tokenUtils';
import FirearmFieldsForm from './FirearmFieldsForm';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';

interface MemberFirearmFormProps {
  initialValues: MemberFirearmDTO;
  onSubmit: (data: MemberFirearmDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const MemberFirearmForm: React.FC<MemberFirearmFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<MemberFirearmDTO>(initialValues);
  const { uploadFile, getFileUrl } = useFileApi();
  const roles = getRoles();

  const { data: firearmOwners = [], isLoading: isLoadingFirearmOwners } = useFirearmOwners();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});


  // Create a wrapper that matches the expected signature
  const setFirearmData = (
    firearmData: MemberFirearmDTO | ((prev: MemberFirearmDTO) => MemberFirearmDTO)
  ) => {
    if (typeof firearmData === 'function') {
      setFormData(firearmData);
    } else {
      setFormData(firearmData);
    }
  };

  const validationRules: ValidationRules = {
    firearmOwnerId: [{ required: true, message: 'Firearm Owner is required' }],
    firearmTypeId: [{ required: true, message: 'Firearm Type is required' }],
    firearmSpecificId: [
      { required: true, message: 'Firearm Specific is required' },
    ],
    firearmMakeId: [{ required: true, message: 'Firearm Make is required' }],
    firearmMakeOther: [{ required: formData.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST, message: 'Firearm Make Other is required' }],
    firearmCaliberId: [
      { required: true, message: 'Firearm Caliber is required' },
    ],
    caliberOther: [
      { required: formData.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST, message: 'Firearm Caliber Other is required' },
    ],
    firearmActionId: [
      {
        required:
          formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM
            ? true
            : false,
        message: 'Firearm Action is required',
      },
    ],
    firearmBarrelId: [
      {
        required:
          formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM
            ? true
            : false,
        message: 'Firearm Barrel is required',
      },
    ],
    firearmPurposeId: [
      {
        required:
          formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM
            ? true
            : false,
        message: 'Firearm Purpose is required',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<MemberFirearmDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  const handleFileChange = (field: keyof MemberFirearmDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'firearms'
        );
        if (response && response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            [field]: response.data
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        {formData.firearmFileId && (
          <ImageLink
            fileId={formData.firearmFileId}
            label="Firearm Photo"
          />
        )}
        <FileUpload
          id="firearmFileId"
          onChange={handleFileChange('firearmFileId')}
          title="Upload Firearm Photo"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          error={errors.firearmFileId}
          isUploading={uploadingFiles['firearmFileId']}
        />
        <FormField
          id="description"
          label="Description"
          type="text"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={false}
          error={errors.description}
        />
        <FormField
          id="firearmOwnerId"
          label="Owner"
          type="dropdown"
          value={formData.firearmOwnerId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Firearm Owner"
          required={true}
          error={errors.firearmOwnerId}
          options={firearmOwners.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingFirearmOwners}
        />
        {formData.firearmOwnerId && (
          <>
            <FirearmFieldsForm formData={formData} setFormData={setFirearmData} errors={errors} />
            {formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM && (
              <>
                <FormField
                  id="applicationRenewalDate"
                  label="Application reference number received"
                  type="date"
                  value={formData.applicationRenewalDate || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Application Renewal Date"
                  required={false}
                  error={errors.applicationRenewalDate}
                />
                <FormField
                  id="applicationNumber"
                  label="Application Number"
                  type="text"
                  value={formData.applicationNumber || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Application Number"
                  required={false}
                  error={errors.applicationNumber}
                />
              </>
            )}
            <FormField
              id="licenceIssueDate"
              label="Licence Issue Date"
              type="date"
              value={formData.licenceIssueDate || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Licence Issue Date"
              required={false}
              error={errors.licenceIssueDate}
            />
            <FormField
              id="licenceExpiryDate"
              label="Licence Expiry Date"
              type="date"
              value={formData.licenceExpiryDate || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Licence Expiry Date"
              required={false}
              error={errors.licenceExpiryDate}
            />
          </>
        )}
        {formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM && (
          <>
            {formData.recieptFileId && (
              <ImageLink
                fileId={formData.recieptFileId}
                label="Receipt"
              />
            )}
            <FileUpload
              id="receiptUpload"
              onChange={handleFileChange('recieptFileId')}
              title="Upload Receipt"
              accept={FileTypeConstants.ALL_SUPPORTED}
              required={false}
              error={errors.recieptFileId}
            />
            <FormField
              id="licenceCardNumber"
              label="Licence Card Number"
              type="text"
              value={formData.licenceCardNumber || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Licence Card Number"
              required={false}
              error={errors.licenceCardNumber}
            />
            <FormField
              id="isNotification"
              label="Firearm Notifications"
              type="checkbox"
              value={formData.isNotification || ''}
              onChange={(e) => handleChange(e, setFormData)}
              required={false}
              error={errors.isNotification}
              helper="Receive firearm expire notifications, please take note we can not guarantee that you will receive these notifications."
            />
          </>
        )}
        {roles?.includes('Admin') && (
          <>
            <FormField
              id="isCompleted"
              label="Completed"
              type="checkbox"
              value={formData.isCompleted || ''}
              onChange={(e) => handleChange(e, setFormData)}
              required={false}
              error={errors.isCompleted}
            />
          </>
        )}
      </FormSection>
    </Form>
  );
};

export default MemberFirearmForm;
